import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import type { ArchiveGroupView, WorkItemSummary } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select } from "../../shared/ui";

/**
 * Filing a batch of finished work away under a name.
 *
 * <p>A name, not a date. Testers clearing down after a release want to find "Release 2.4
 * verified" again in six months, and "archived on the 14th of May" is something they would have
 * to work out first.
 *
 * <p>Either a new name or a group that already exists. Both are the same request, because a
 * group with nothing in it is not a thing anybody wants — and two requests would leave a group
 * with no contents behind whenever the second one failed.
 */
export function MoveToGroupDrawer({
  projectId,
  items,
  open,
  onClose,
  onDone,
}: {
  projectId: string;
  /** The selected rows, so the drawer can say what is about to move. */
  items: WorkItemSummary[];
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  /* "" means a new group. Anything else is an existing group's id. */
  const [groupId, setGroupId] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const cache = useQueryClient();

  const groups = useQuery({
    queryKey: ["archive-groups", projectId],
    queryFn: () =>
      api.get<ArchiveGroupView[]>(`/api/v1/work-archive-groups?projectId=${projectId}`),
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setGroupId("");
      setName("");
      setNote("");
      setError(null);
    }
  }, [open]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/work-archive-groups?projectId=${projectId}`, {
        groupId: groupId || null,
        name: groupId ? null : name.trim(),
        note: note.trim() || null,
        workItemIds: items.map((item) => item.id),
      });
      /*
       * The chooser on the list behind this drawer reads the same key, and a batch filed into a
       * brand-new group would otherwise leave it showing the groups that existed a moment ago —
       * with no way to open the one just created short of reloading the page.
       */
      await cache.invalidateQueries({ queryKey: ["archive-groups", projectId] });
      onDone();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Could not file those items away.",
      );
    } finally {
      setSaving(false);
    }
  };

  const existing = groups.data ?? [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Move finished work into a group"
      subtitle={`${items.length} item${items.length === 1 ? "" : "s"} will come off the lists and sit under the name you give here.`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void save()}
            loading={saving}
            disabled={!groupId && name.trim() === ""}
          >
            Move {items.length}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {existing.length > 0 && (
          <Field
            label="Group"
            hint="Add to a batch somebody already filed, or start a new one."
          >
            <Select value={groupId} onChange={(event) => setGroupId(event.target.value)}>
              <option value="">A new group…</option>
              {existing.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name} ({group.itemCount})
                </option>
              ))}
            </Select>
          </Field>
        )}

        {!groupId && (
          <>
            <Field
              label="Name"
              hint="What somebody searching for this batch in six months would type."
            >
              <Input
                placeholder="Release 2.4 verified"
                value={name}
                maxLength={200}
                onChange={(event) => setName(event.target.value)}
              />
            </Field>
            <Field label="Note" hint="Optional, for when the name alone will not say it.">
              <Input
                placeholder="Signed off by QA on 12 May"
                value={note}
                maxLength={2000}
                onChange={(event) => setNote(event.target.value)}
              />
            </Field>
          </>
        )}

        {/* Said plainly, because this is the part people worry about. */}
        <p className="text-sm text-ink-600">
          Nothing is deleted. The group keeps its contents, and anything in it can be reopened —
          it goes back to the stage it was at before it was finished.
        </p>

        <div className="rounded-lg border border-ink-200 bg-ink-50/60 p-3">
          <ul className="space-y-1">
            {items.map((item) => (
              <li key={item.id} className="text-sm text-ink-700">
                <span className="font-mono text-xs text-ink-500">{item.itemKey}</span>{" "}
                {item.title}
              </li>
            ))}
          </ul>
        </div>

        {error && <InlineAlert tone="error">{error}</InlineAlert>}
      </div>
    </Drawer>
  );
}
